import React from 'react';
import { Code, Copy, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';

const ApiDocs = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Code className="h-8 w-8 text-blue-500" />
          <div>
            <h1 className="text-2xl font-bold text-white">API Documentation</h1>
            <p className="text-sm text-gray-400">Integrate security features into your applications</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline">
            <ExternalLink className="h-4 w-4 mr-2" />
            API Reference
          </Button>
          <Button>Get API Key</Button>
        </div>
      </div>

      {/* Quick Start */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Quick Start</h2>
        <div className="space-y-4">
          <div className="p-4 bg-gray-800/30 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-white">Authentication</span>
              <Button variant="outline" size="sm">
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
            </div>
            <pre className="text-sm text-gray-300 font-mono bg-gray-900/50 p-4 rounded-lg overflow-x-auto">
              {`curl -X GET "https://api.example.com/v1/assets" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
            </pre>
          </div>

          <div className="grid grid-cols-3 gap-6">
            {['Assets', 'Findings', 'Scans'].map((endpoint) => (
              <div key={endpoint} className="p-4 bg-gray-800/30 rounded-lg">
                <h3 className="text-sm font-medium text-white mb-2">{endpoint} API</h3>
                <p className="text-xs text-gray-400 mb-4">
                  Manage and retrieve {endpoint.toLowerCase()} data.
                </p>
                <Button variant="outline" size="sm" className="w-full">
                  View Documentation
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* API Examples */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">Code Examples</h2>
        <div className="space-y-4">
          {['Python', 'Node.js', 'Go'].map((lang) => (
            <div key={lang} className="p-4 bg-gray-800/30 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-white">{lang}</span>
                <Button variant="outline" size="sm">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
              </div>
              <pre className="text-sm text-gray-300 font-mono bg-gray-900/50 p-4 rounded-lg overflow-x-auto">
                {`# Example ${lang} code
# Initialize the client
client = SecurityClient(api_key="YOUR_API_KEY")

# Get all assets
assets = client.assets.list()

# Start a security scan
scan = client.scans.create(target="example.com")`}
              </pre>
            </div>
          ))}
        </div>
      </div>

      {/* SDKs and Libraries */}
      <div className="bg-[#0F1629] rounded-xl p-6 border border-gray-800/50">
        <h2 className="text-lg font-semibold text-white mb-4">SDKs & Libraries</h2>
        <div className="grid grid-cols-3 gap-6">
          {['Python SDK', 'Node.js SDK', 'Go SDK'].map((sdk) => (
            <div key={sdk} className="p-4 bg-gray-800/30 rounded-lg">
              <h3 className="text-sm font-medium text-white mb-2">{sdk}</h3>
              <p className="text-xs text-gray-400 mb-4">
                Official SDK for {sdk} integration.
              </p>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="sm">Documentation</Button>
                <Button variant="outline" size="sm">GitHub</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ApiDocs;